// De app-schil: de drie kolommen van de mockup, het navigeren ertussen, en alles wat er aan
// gegevens omheen hangt. De editor zit apart (onderdelen/Editor.tsx).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api, GeenVerbinding, NietAangemeld,
  type Blok, type Boek, type EigenschapRegel, type KluisStand, type Notitie, type Rij, type TagRegel, type Zoekopdracht, type Backlink
} from "./api.ts";
import {
  bewaarLokaal, bewaarNotitie, bewaarRijen, bewaarSpiegelStand, haalNotitieUitCache, haalRijen,
  lijstUitSpiegel, opNotitie, opSync, spiegelStand, verwerkWachtrij, vergeetNotitie, vulSpiegel,
  wachtrijIds, zetInWachtrij, type SyncStand
} from "./opslag.ts";
import { ulid } from "./ulid.ts";
import { Editor } from "./onderdelen/Editor.tsx";
import { Icoon } from "./onderdelen/Icoon.tsx";
import { Palet, type PaletActie } from "./onderdelen/Palet.tsx";
import { Sneltoetsen } from "./onderdelen/Sneltoetsen.tsx";
import { Instellingen } from "./onderdelen/Instellingen.tsx";
import { Versies } from "./onderdelen/Versies.tsx";
import { Overzicht } from "./onderdelen/Overzicht.tsx";
import { Bijlagen } from "./onderdelen/Bijlagen.tsx";
import { Taken } from "./onderdelen/Taken.tsx";
import { Eigenschappen } from "./onderdelen/Eigenschappen.tsx";
import { Bord, Tabel, WeergaveKiezer } from "./onderdelen/Weergaven.tsx";
import { OpSlot } from "./onderdelen/Kluis.tsx";
import * as Kluis from "./kluis.ts";
import { ClipOntvangst } from "./onderdelen/ClipOntvangst.tsx";
import type { Editor as TipTapEditor } from "@tiptap/react";

const LEGE_INHOUD: Blok = { type: "doc", content: [{ type: "paragraph" }] };

type Melding = { tekst: string; knop?: string; doe?: () => void; waarschuwing?: boolean };

/** Hoe je naar de lijst kijkt. Bord en tabel groeperen op een eigenschap (O-13). */
type Weergave = "lijst" | "kaarten" | "bord" | "tabel";

function tijdKort(iso: string): string {
  const datum = new Date(iso);
  const vandaag = new Date();
  const zelfdeDag = datum.toDateString() === vandaag.toDateString();
  if (zelfdeDag) return datum.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  const ditJaar = datum.getFullYear() === vandaag.getFullYear();
  return datum.toLocaleDateString("nl-NL", { day: "numeric", month: "short", ...(ditJaar ? {} : { year: "numeric" }) });
}

/**
 * Naar het inlogscherm. De app-schil staat búiten Authentik (anders kan de PWA niet
 * installeren), dus bij de eerste keer openen laadt de pagina wél en mislukt alleen de API.
 * Dan hoort de app je meteen naar de aanmelding te sturen in plaats van een lege lijst te
 * tonen — dat was precies wat er bij de eerste echte poging op de NAS misging.
 *
 * Eén poging per tabblad: lukt aanmelden niet, dan blijft er een melding staan in plaats van
 * een lus van doorverwijzingen.
 */
function naarAanmelden(): boolean {
  try {
    if (sessionStorage.getItem("notities-aanmeldpoging")) return false;
    sessionStorage.setItem("notities-aanmeldpoging", "1");
  } catch {
    /* zonder sessionStorage doen we het één keer en vertrouwen we op de gebruiker */
  }
  location.href = `/aanmelden?terug=${encodeURIComponent(location.pathname + location.search)}`;
  return true;
}

function groepVan(rij: Rij): string {
  if (rij.gepind) return "Gepind";
  const datum = new Date(rij.gewijzigd);
  const nu = new Date();
  const dagen = Math.floor((nu.getTime() - datum.getTime()) / 86_400_000);
  if (datum.toDateString() === nu.toDateString()) return "Vandaag";
  if (dagen <= 1) return "Gisteren";
  if (dagen <= 30) return "Vorige 30 dagen";
  return "Ouder";
}

export function App() {
  const [pad, setPad] = useState(() => location.pathname + location.search);
  const [ik, setIk] = useState<{ gebruiker: { naam: string }; inbox: string } | null>(null);
  const [boeken, setBoeken] = useState<Boek[]>([]);
  const [rijen, setRijen] = useState<Rij[]>([]);
  const [boek, setBoek] = useState<string | undefined>(undefined);
  const [sorteer, setSorteer] = useState("gewijzigd");
  const [open, setOpen] = useState<Notitie | null>(null);
  const [zoekterm, setZoekterm] = useState<string | undefined>(undefined);
  const [sync, setSync] = useState<SyncStand>({
    bezig: false, wachtend: 0, offline: false, afgemeld: false, laatsteFout: null, laatsteMelding: null
  });
  const [melding, setMelding] = useState<Melding | null>(null);
  const [paletStand, setPaletStand] = useState<"dicht" | "zoek" | "kies">("dicht");
  const [zijbalk, setZijbalk] = useState(false);
  const [nieuwTekst, setNieuwTekst] = useState("");
  const [tags, setTags] = useState<TagRegel[]>([]);
  const [tag, setTag] = useState<string | undefined>(undefined);
  const [zoekopdrachten, setZoekopdrachten] = useState<Zoekopdracht[]>([]);
  const [weergave, setWeergave] = useState<Weergave>(
    () => (localStorage.getItem("notities-weergave") as Weergave) ?? "lijst"
  );
  // Waarop het bord groepeert (en welke kolom de tabel toont). Per apparaat onthouden, net als
  // de weergave zelf.
  const [groepeerOp, setGroepeerOp] = useState(() => localStorage.getItem("notities-groepeer") ?? "");
  const [backlinks, setBacklinks] = useState<Backlink[]>([]);
  const [sjablonen, setSjablonen] = useState<Rij[]>([]);
  const [sneltoetsen, setSneltoetsen] = useState(false);
  const [versiesOpen, setVersiesOpen] = useState(false);
  const [overzichtOpen, setOverzichtOpen] = useState(false);
  // Hoeveel taken er open staan; het getal naast "Taken" in de zijbalk.
  const [takenOpen, setTakenOpen] = useState<number | null>(null);
  // Welke eigenschappen je elders al gebruikt; voor de keuzelijstjes en de bordweergave.
  const [eigenschappen, setEigenschappen] = useState<EigenschapRegel[]>([]);
  // De kluis: het zout en het controleblokje van de server, en of hij op dit moment open is.
  // De sleutel zelf staat in kluis.ts en alleen in het geheugen van dit tabblad.
  const [kluisStand, setKluisStand] = useState<KluisStand | null>(null);
  const [kluisOpen, setKluisOpen] = useState(Kluis.isOpen());
  // De ontsleutelde inhoud van de notitie die je openhebt; nooit naar IndexedDB.
  const [kluisInhoud, setKluisInhoud] = useState<Blok | null>(null);
  // Welke notitie hangt er op dit moment aan de muis? Alleen dan zijn de notitieboeken in de
  // zijbalk een landingsplek. Ook als ref, want bij het loslaten moet de id er meteen zijn:
  // wachten op een nieuwe ronde van React kan bij een snelle sleep net te laat zijn.
  const gesleeptRef = useRef<string | null>(null);
  const [gesleept, setGesleept] = useState<string | null>(null);
  const onthoudSleep = useCallback((id: string | null) => {
    gesleeptRef.current = id;
    setGesleept(id);
  }, []);
  const editorRef = useRef<TipTapEditor | null>(null);
  const tagKlok = useRef<number | null>(null);
  // De laatst bekende versie per notitie. Die sturen we mee bij het bewaren, zodat de server
  // kan zien of een ander apparaat eerder was.
  const revs = useRef(new Map<string, number>());

  const schermSoort = pad.startsWith("/notitie/") ? "notitie" : "lijst";

  const ga = useCallback((nieuwPad: string) => {
    history.pushState(null, "", nieuwPad);
    setPad(nieuwPad);
    setZijbalk(false);
  }, []);

  // --- gegevens laden -------------------------------------------------------------------

  const laadLijst = useCallback(
    async (welkBoek?: string, hoe: string = sorteer, welkeTag?: string) => {
      const sleutel = `${welkBoek ?? "alles"}:${welkeTag ?? ""}:${hoe}`;
      try {
        const { rijen } = await api.lijst({ boek: welkBoek, tag: welkeTag, sorteer: hoe, limiet: 200 });
        setRijen(rijen);
        void bewaarRijen(sleutel, rijen);
        setSync((s) => (s.offline ? { ...s, offline: false } : s));
      } catch (fout) {
        if (fout instanceof GeenVerbinding) {
          // Zichtbaar maken dát je offline bent. De wachtrij merkt het alleen als er iets te
          // versturen valt; lezen mislukt eerder, en dan hoort de stip het ook te zeggen.
          setSync((s) => (s.offline ? s : { ...s, offline: true }));
          // Uit de eigen kopie, met dezelfde filters en volgorde. Pas als die leeg is vallen
          // we terug op de lijst zoals hij de vorige keer op het scherm stond.
          const uitSpiegel = await lijstUitSpiegel({ boek: welkBoek, tag: welkeTag, sorteer: hoe, limiet: 200 });
          if (uitSpiegel.length > 0) setRijen(uitSpiegel);
          else {
            const uitCache = await haalRijen(sleutel);
            if (uitCache) setRijen(uitCache);
          }
        } else if (fout instanceof NietAangemeld) {
          if (!naarAanmelden()) setSync((s) => ({ ...s, afgemeld: true }));
        }
      }
    },
    [sorteer]
  );

  useEffect(() => {
    void (async () => {
      try {
        const [mij, boom] = await Promise.all([api.ik(), api.boom()]);
        setIk(mij);
        setBoeken(boom.boeken);
        // Aanmelden is gelukt: de poging-vlag weer opruimen, zodat een volgende verlopen
        // sessie opnieuw netjes naar het inlogscherm gaat.
        try {
          sessionStorage.removeItem("notities-aanmeldpoging");
        } catch {
          /* niets aan te doen */
        }
      } catch (fout) {
        if (fout instanceof NietAangemeld) {
          if (naarAanmelden()) return; // we gaan weg; de rest hoeft niet meer
          setSync((s) => ({ ...s, afgemeld: true }));
        }
        if (fout instanceof GeenVerbinding) setSync((s) => ({ ...s, offline: true }));
      }
      void laadLijst(undefined);
      void ververs();

      // De spiegel: alle notities op dit apparaat, zodat lezen en zoeken ook zonder netwerk
      // werken. Stond hij er al, dan pakken we het volgnummer op waar we gebleven waren en
      // halen we alleen op wat er sindsdien is veranderd.
      const stand = await spiegelStand();
      if (stand?.gevuld) {
        volgnr.current = stand.volgnr;
        void haalNieuws();
      } else {
        const uit = await vulSpiegel();
        if (uit) volgnr.current = uit.volgnr;
        void haalNieuws();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tags en bewaarde zoekopdrachten horen bij de zijbalk; ze veranderen zodra je schrijft.
  const ververs = useCallback(async () => {
    try {
      const [tagLijst, bewaard, sjabloonLijst, takenLijst, eigenLijst] = await Promise.all([
        api.tags(),
        api.zoekopdrachten(),
        api.sjablonen(),
        api.taken(false),
        api.eigenschappen()
      ]);
      setTags(tagLijst.tags);
      setZoekopdrachten(bewaard.zoekopdrachten);
      setSjablonen(sjabloonLijst.rijen);
      setTakenOpen(takenLijst.open);
      setEigenschappen(eigenLijst.sleutels);
      setKluisStand(await api.kluis());
    } catch {
      /* zonder verbinding blijft de zijbalk staan zoals hij stond */
    }
  }, []);

  // --- wat er elders is gebeurd ---------------------------------------------------------
  //
  // De app keek nooit terug: gooide je op de laptop een notitie weg, dan bleef hij op de
  // telefoon staan tot je de app helemaal afsloot en opnieuw opende. De server houdt van elke
  // wijziging een volgnummer bij; we vragen simpelweg wat er sinds het vorige nummer is
  // gebeurd. Dat is één klein verzoek, ook als er niets veranderd is.
  const volgnr = useRef(0);
  const [herlaadEditor, setHerlaadEditor] = useState(0);

  const haalNieuws = useCallback(async () => {
    try {
      const nieuws = await api.wijzigingen(volgnr.current);
      volgnr.current = nieuws.volgnr;
      // Het volgnummer bij de spiegel bewaren: na een herstart kijkt de app verder vanaf dit
      // punt in plaats van alles opnieuw op te halen. Zonder volgnummer geeft de server
      // alleen het ijkpunt terug en is er dus ook niets toe te passen.
      void bewaarSpiegelStand(nieuws.volgnr);
      if (nieuws.notities.length === 0 && nieuws.verwijderd.length === 0) return;

      // Wat hier nog in de wachtrij staat is nieuwer dan wat de server vertelt; dat mag de
      // kopie op dit apparaat niet overschrijven.
      const wacht = await wachtrijIds();
      const openId = open?.id;
      let openIsGewijzigd = false;

      for (const notitie of nieuws.notities) {
        if (wacht.has(notitie.id)) continue;
        // De notitie die je op dit moment openhebt niet onder je handen vervangen; dat zou
        // je cursor laten springen en je laatste zin kunnen overschrijven. We zeggen het,
        // en jij kiest.
        if (notitie.id === openId) {
          openIsGewijzigd = true;
          continue;
        }
        await bewaarNotitie(notitie);
      }
      for (const id of nieuws.verwijderd) if (!wacht.has(id)) await vergeetNotitie(id);

      if (openId && nieuws.verwijderd.includes(openId) && !wacht.has(openId)) {
        setOpen(null);
        ga("/");
        setMelding({ tekst: "Deze notitie is op een ander apparaat weggegooid." });
      } else if (openIsGewijzigd) {
        setMelding({
          tekst: "Deze notitie is op een ander apparaat gewijzigd.",
          knop: "Opnieuw laden",
          doe: async () => {
            setMelding(null);
            const vers = await api.haal(openId!);
            await bewaarNotitie(vers);
            setOpen(vers);
            // De editor leest zijn inhoud bij het openen; een nieuwe sleutel laat hem
            // opnieuw beginnen met de verse tekst.
            setHerlaadEditor((n) => n + 1);
          }
        });
      }

      await laadLijst(boek, sorteer, tag);
      void ververs();
      // Ook de notitieboeken: een notitie die elders in een boek is gelegd verandert de
      // tellingen in de zijbalk.
      const boom = await api.boom().catch(() => null);
      if (boom) setBoeken(boom.boeken);
    } catch {
      /* offline of afgemeld: de wachtrij merkt dat vanzelf, hier blijft alles staan */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boek, sorteer, tag, open?.id, laadLijst, ga]);

  // Kijken zodra je de app weer voor je neus hebt, en verder elke halve minuut. Niet terwijl
  // hij op de achtergrond staat: dat kost alleen batterij.
  useEffect(() => {
    const kijk = () => {
      if (document.visibilityState === "visible") void haalNieuws();
    };
    addEventListener("visibilitychange", kijk);
    addEventListener("focus", kijk);
    const klok = window.setInterval(kijk, 30_000);
    return () => {
      removeEventListener("visibilitychange", kijk);
      removeEventListener("focus", kijk);
      window.clearInterval(klok);
    };
  }, [haalNieuws]);

  useEffect(() => opSync(setSync), []);

  // Zodra de wachtrij iets heeft weggeschreven, geeft de server de nieuwe rev terug. Die
  // moeten we bijhouden, anders stuurt het volgende bewaren een verouderde rev mee en
  // krijgen we een botsing met onszelf.
  useEffect(
    () =>
      opNotitie((notitie) => {
        const bekend = revs.current.get(notitie.id) ?? 0;
        if (notitie.rev >= bekend) revs.current.set(notitie.id, notitie.rev);
        // Alleen wat de server uitrekent overnemen (rev, tags, taken) — niet de tekst zelf:
        // die is hier nieuwer, want je bent er net in aan het typen.
        setOpen((huidig) =>
          huidig && huidig.id === notitie.id
            ? { ...huidig, rev: notitie.rev, tags: notitie.tags ?? [], taken: notitie.taken }
            : huidig
        );
        setRijen((lijst) =>
          lijst.map((r) =>
            r.id === notitie.id
              ? {
                  ...r, titel: notitie.titel, gepind: notitie.gepind, gewijzigd: notitie.gewijzigd,
                  taken: notitie.taken, tags: notitie.tags ?? []
                }
              : r
          )
        );
        // De tagbrowser in de zijbalk hoort mee te bewegen met wat je typt. Niet bij elke
        // toetsaanslag: een seconde nadat het bewaren klaar is, is ruim op tijd.
        if (tagKlok.current) window.clearTimeout(tagKlok.current);
        tagKlok.current = window.setTimeout(() => void ververs(), 1000);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    const opTerug = () => setPad(location.pathname + location.search);
    addEventListener("popstate", opTerug);
    return () => removeEventListener("popstate", opTerug);
  }, []);

  // --- routes --------------------------------------------------------------------------

  useEffect(() => {
    const [schoonPad, vraag] = pad.split("?");

    if (schoonPad.startsWith("/notitie/")) {
      const id = schoonPad.slice("/notitie/".length);
      setBacklinks([]);
      void api
        .backlinks(id)
        .then((u) => setBacklinks(u.backlinks))
        .catch(() => setBacklinks([]));
      void (async () => {
        const uitCache = await haalNotitieUitCache(id);
        if (uitCache) setOpen(uitCache);
        try {
          const notitie = await api.haal(id);
          revs.current.set(notitie.id, notitie.rev);
          setOpen(notitie);
          void bewaarNotitie(notitie);
        } catch (fout) {
          if (fout instanceof NietAangemeld) {
            if (!naarAanmelden()) setSync((s) => ({ ...s, afgemeld: true }));
          }
          if (!uitCache && fout instanceof GeenVerbinding) {
            setMelding({ tekst: "Deze notitie staat nog niet op dit apparaat en er is geen verbinding." });
          }
        }
      })();
      return;
    }

    if (schoonPad === "/vandaag") {
      void (async () => {
        try {
          const notitie = await api.vandaag();
          void bewaarNotitie(notitie);
          history.replaceState(null, "", `/notitie/${notitie.id}`);
          setPad(`/notitie/${notitie.id}`);
        } catch (fout) {
          // Niet aangemeld is geen fout van de dagnotitie; dan stuurt de app je gewoon naar
          // het inlogscherm in plaats van een raadselachtige melding te tonen.
          if (fout instanceof NietAangemeld) {
            if (!naarAanmelden()) setSync((s) => ({ ...s, afgemeld: true }));
            return;
          }
          setMelding({
            tekst:
              fout instanceof GeenVerbinding
                ? "Geen verbinding, dus de dagnotitie van vandaag kan nog niet worden gemaakt."
                : "De dagnotitie kon niet worden geopend."
          });
          ga("/");
        }
      })();
      return;
    }

    if (schoonPad === "/zoeken") {
      setPaletStand("zoek");
      history.replaceState(null, "", "/");
      setPad("/");
      return;
    }

    if (schoonPad === "/" && new URLSearchParams(vraag ?? "").get("nieuw")) {
      history.replaceState(null, "", "/");
      void nieuweNotitie("");
      return;
    }

    setOpen(null);
    setZoekterm(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pad]);

  /** Een notitie naar een andere kolom van het bord slepen: die eigenschap krijgt die waarde. */
  const zetEigenschap = useCallback(
    async (id: string, sleutel: string, waarde: string) => {
      const rij = rijen.find((r) => r.id === id);
      const velden = { ...(rij?.eigenschappen ?? {}) };
      if (waarde) velden[sleutel] = waarde;
      else delete velden[sleutel];
      // Meteen op het scherm; het wachten op de NAS hoort niet zichtbaar te zijn bij een sleep.
      setRijen((lijst) => lijst.map((r) => (r.id === id ? { ...r, eigenschappen: velden } : r)));
      try {
        const notitie = await api.zetEigenschappen(id, velden);
        await bewaarNotitie(notitie);
        void ververs();
      } catch (fout) {
        setRijen((lijst) => lijst.map((r) => (r.id === id ? { ...r, eigenschappen: rij?.eigenschappen ?? {} } : r)));
        setMelding({ tekst: `Verplaatsen lukte niet: ${(fout as Error).message}`, waarschuwing: true });
      }
    },
    [rijen, ververs]
  );

  useEffect(() => Kluis.opSlot(setKluisOpen), []);

  // De inhoud van een kluisnotitie ontsleutelen zodra je hem openhebt én de kluis open is.
  // Mislukt dat, dan laten we hem op slot staan: liever geen tekst dan de verkeerde.
  useEffect(() => {
    let nogGeldig = true;
    setKluisInhoud(null);
    const sleutel = Kluis.huidigeSleutel();
    if (!open?.kluis || !sleutel || !Kluis.isVersleuteld(open.inhoud)) return;
    void Kluis.ontsleutel(sleutel, open.inhoud)
      .then((doc) => {
        if (nogGeldig) {
          setKluisInhoud(doc);
          Kluis.houdOpen();
        }
      })
      .catch(() => setMelding({ tekst: "Deze notitie hoort bij een ander kluiswachtwoord.", waarschuwing: true }));
    return () => {
      nogGeldig = false;
    };
  }, [open?.id, open?.rev, kluisOpen]);

  /**
   * Een notitie in de kluis zetten of eruit halen.
   *
   * Erin: de tekst wordt hier versleuteld en zo naar de server gestuurd. Eruit: de ontsleutelde
   * tekst gaat er gewoon weer in te staan. Allebei via de normale weg van een wijziging, zodat
   * er een versie bij komt en de andere apparaten het oppikken.
   */
  const wisselKluis = useCallback(
    async (notitie: Notitie, erin: boolean) => {
      const sleutel = Kluis.huidigeSleutel();
      if (!kluisStand?.ingesteld) {
        setMelding({ tekst: "Richt eerst de kluis in bij Instellingen.", waarschuwing: true });
        return;
      }
      if (!sleutel) {
        setMelding({ tekst: "De kluis staat op slot. Open hem eerst bij Instellingen.", waarschuwing: true });
        return;
      }
      // Eén keer vragen, want dit is niet terug te draaien: de versies van vóór het versleutelen
      // worden weggegooid. Laten staan zou betekenen dat je wachtwoord op slot zit terwijl de
      // vorige stand ernaast leesbaar blijft — dan hoef je de kluis niet te hebben.
      if (erin && !window.confirm("In de kluis zetten wist de versiegeschiedenis van deze notitie; die staat nog onversleuteld op de server. Doorgaan?")) {
        return;
      }
      try {
        const inhoud = erin
          ? await Kluis.versleutel(sleutel, kluisInhoud ?? notitie.inhoud, kluisStand.zout, kluisStand.rondes)
          : (kluisInhoud ?? (await Kluis.ontsleutel(sleutel, notitie.inhoud)));
        const bijgewerkt = await api.wijzig(notitie.id, { inhoud, kluis: erin }, revs.current.get(notitie.id));
        revs.current.set(bijgewerkt.id, bijgewerkt.rev);
        await bewaarNotitie(bijgewerkt);
        setOpen(bijgewerkt);
        setKluisInhoud(erin ? (kluisInhoud ?? notitie.inhoud) : null);
        setRijen((lijst) => lijst.map((r) => (r.id === notitie.id ? { ...r, kluis: erin, fragment: erin ? "" : r.fragment } : r)));
        setKluisStand(await api.kluis());
        setHerlaadEditor((n) => n + 1);
        setMelding({ tekst: erin ? "Deze notitie staat nu in de kluis" : "Deze notitie staat niet meer in de kluis" });
      } catch (fout) {
        setMelding({ tekst: `Dat lukte niet: ${(fout as Error).message}`, waarschuwing: true });
      }
    },
    [kluisStand, kluisInhoud]
  );

  // --- schrijven ------------------------------------------------------------------------

  const nieuweNotitie = useCallback(
    async (titel: string, inBoek?: string, uitSjabloon?: Blok | null) => {
      const id = ulid();
      const nu = new Date().toISOString();
      const inhoud = uitSjabloon ?? LEGE_INHOUD;
      const notitie: Notitie = {
        id, soort: "notitie", ouder: inBoek ?? boek ?? null, titel, inhoud,
        gepind: false, kleur: null, sortering: "", systeem: null, dag: null, rev: 1, sjabloon: false,
        aangemaakt: nu, gewijzigd: nu, verwijderd: null, gearchiveerd: null, taken: { af: 0, totaal: 0 }, tags: [],
        eigenschappen: {}, kluis: false
      };
      revs.current.set(id, 1);
      setOpen(notitie);
      setRijen((lijst) => [
        { id, titel, fragment: "", gepind: false, kleur: null, ouder: notitie.ouder, gewijzigd: nu, aangemaakt: nu, taken: { af: 0, totaal: 0 }, tags: [], eigenschappen: {}, kluis: false },
        ...lijst
      ]);
      await bewaarNotitie(notitie);
      await zetInWachtrij({ soort: "maak", id, lading: { titel, inhoud, boek: notitie.ouder }, tijd: nu });
      ga(`/notitie/${id}`);
    },
    [boek, ga]
  );

  /**
   * Een wijziging wegschrijven. De id staat er altijd expliciet bij, want dit wordt ook
   * aangeroepen voor de notitie die je net verlaten hebt — en die is dan niet meer degene
   * die openstaat.
   */
  const bewaarWijziging = useCallback(
    (id: string, patch: { titel?: string; inhoud?: Blok | null; gepind?: boolean; boek?: string | null; kleur?: string | null; sjabloon?: boolean }) => {
      const nu = new Date().toISOString();
      const { boek, ...rest } = patch;

      // Een kluisnotitie gaat versleuteld de deur uit — ook naar de kopie op dit apparaat. Wat
      // je op het scherm ziet blijft de leesbare tekst, en die staat alleen in het geheugen.
      if (open?.id === id && open.kluis && patch.inhoud !== undefined) {
        setKluisInhoud(patch.inhoud);
        const sleutel = Kluis.huidigeSleutel();
        if (!sleutel || !kluisStand?.ingesteld) {
          setMelding({ tekst: "De kluis staat op slot; deze wijziging is niet bewaard.", waarschuwing: true });
          return;
        }
        setRijen((lijst) => lijst.map((r) => (r.id === id ? { ...r, gewijzigd: nu } : r)));
        void (async () => {
          const versleuteld = await Kluis.versleutel(sleutel, patch.inhoud ?? null, kluisStand.zout, kluisStand.rondes);
          const uitCache = await haalNotitieUitCache(id);
          if (uitCache) await bewaarLokaal({ ...uitCache, ...patch, inhoud: versleuteld, gewijzigd: nu });
          await zetInWachtrij({
            soort: "wijzig",
            id,
            lading: { ...patch, inhoud: versleuteld } as Record<string, unknown>,
            rev: revs.current.get(id),
            tijd: nu
          });
          Kluis.houdOpen();
        })();
        return;
      }

      setOpen((huidig) =>
        huidig && huidig.id === id ? { ...huidig, ...rest, ...(boek !== undefined ? { ouder: boek } : {}), gewijzigd: nu } : huidig
      );
      setRijen((lijst) => lijst.map((r) => (r.id === id ? { ...r, ...rest, gewijzigd: nu } : r)));
      void (async () => {
        const uitCache = await haalNotitieUitCache(id);
        // `bewaarLokaal` en niet `bewaarNotitie`: dit is jouw tekst, niet die van de server.
        // De basis waartegen we straks samenvoegen blijft zo staan zoals de server hem gaf.
        if (uitCache) await bewaarLokaal({ ...uitCache, ...patch, gewijzigd: nu });
        await zetInWachtrij({
          soort: "wijzig",
          id,
          lading: patch as Record<string, unknown>,
          rev: revs.current.get(id),
          tijd: nu
        });
      })();
    },
    // `open` en `kluisStand` staan erbij omdat een kluisnotitie eerst versleuteld moet worden;
    // zonder deze twee zou hij met een verouderde sleutel of vlag werken.
    [open, kluisStand]
  );

  /**
   * Een notitie in een notitieboek leggen. Dit hangt aan twee dingen: de keuzelijst boven de
   * notitie, en slepen vanuit de lijst naar een boek in de zijbalk. Dat slepen was de vraag —
   * de keuzelijst zag niemand staan.
   */
  function verplaatsNaarBoek(id: string, boekId: string | null, vorigeBekend?: string | null) {
    // Bij "ongedaan maken" is de vorige plek al bekend; de rij staat dan niet meer in beeld.
    const vorige =
      vorigeBekend !== undefined ? vorigeBekend : (rijen.find((r) => r.id === id)?.ouder ?? (open?.id === id ? open.ouder : null));
    if (vorige === boekId) return;

    // Kijk je in een notitieboek, dan hoort hij daar nu niet meer bij te staan.
    setRijen((lijst) =>
      boek && boekId !== boek ? lijst.filter((r) => r.id !== id) : lijst.map((r) => (r.id === id ? { ...r, ouder: boekId } : r))
    );

    bewaarWijziging(id, { boek: boekId });
    // De tellingen in de zijbalk meteen zelf bijstellen. Ze bij de server ophalen kan hier
    // niet: schrijven gaat via de wachtrij, dus een tel later pas, en dan stond er nog even
    // de oude stand. De ronde die kijkt wat er elders veranderd is haalt ze later wel op.
    setBoeken((huidig) => metAangepasteTelling(huidig, boekId, vorige));
    const naam = boekId ? (boeken.flatMap(plat).find((b) => b.id === boekId)?.titel ?? "een notitieboek") : "Losse notities";
    setMelding({
      tekst: `Verplaatst naar ${naam}`,
      knop: "Ongedaan maken",
      doe: () => {
        setMelding(null);
        verplaatsNaarBoek(id, vorige, boekId);
      }
    });
  }

  const verwijder = useCallback(
    async (id: string, titel: string) => {
      setRijen((lijst) => lijst.filter((r) => r.id !== id));
      if (open?.id === id) ga("/");
      await vergeetNotitie(id);
      await zetInWachtrij({ soort: "verwijder", id, tijd: new Date().toISOString() });
      setMelding({
        tekst: `"${titel || "Zonder titel"}" is naar de prullenbak`,
        knop: "Ongedaan maken",
        doe: async () => {
          await zetInWachtrij({ soort: "herstel", id, tijd: new Date().toISOString() });
          setMelding(null);
          // Even wachten tot de wachtrij het echt heeft gedaan, anders laadt de lijst de
          // oude stand terug.
          await verwerkWachtrij();
          void laadLijst(boek);
        }
      });
    },
    [open, ga, boek, laadLijst]
  );

  // --- sneltoetsen ----------------------------------------------------------------------

  useEffect(() => {
    function opToets(g: KeyboardEvent) {
      const inVeld = (g.target as HTMLElement)?.closest("input, textarea, [contenteditable='true']");
      if ((g.ctrlKey || g.metaKey) && g.key.toLowerCase() === "k") {
        g.preventDefault();
        setPaletStand("zoek");
        return;
      }
      if (!inVeld && g.key === "c") {
        g.preventDefault();
        void nieuweNotitie("");
      }
      if (!inVeld && (g.key === "?" || (g.shiftKey && g.key === "/"))) {
        g.preventDefault();
        setSneltoetsen(true);
      }
    }
    addEventListener("keydown", opToets);
    return () => removeEventListener("keydown", opToets);
  }, [nieuweNotitie]);

  // --- schermen -------------------------------------------------------------------------

  if (pad.startsWith("/vastleggen")) {
    return <Vastleggen inbox={ik?.inbox ?? null} ga={ga} />;
  }

  if (pad.startsWith("/clip")) {
    return <ClipOntvangst ga={ga} />;
  }

  if (pad.startsWith("/bijlagen")) {
    return <Bijlagen terug={() => ga("/")} opNotitie={(id) => ga(`/notitie/${id}`)} />;
  }

  if (pad.startsWith("/instellingen")) {
    return <Instellingen terug={() => { ga("/"); void ververs(); }} />;
  }

  const acties: PaletActie[] = [
    { naam: "Nieuwe notitie", icoon: "plus", toets: "c", doe: () => { setPaletStand("dicht"); void nieuweNotitie(""); } },
    { naam: "Vandaag openen", icoon: "kalender", doe: () => { setPaletStand("dicht"); ga("/vandaag"); } },
    { naam: "Alles exporteren als markdown", icoon: "download", doe: () => { setPaletStand("dicht"); location.href = "/api/export"; } },
    { naam: "Taken uit alle notities", icoon: "taken", toets: "t", doe: () => { setPaletStand("dicht"); ga("/taken"); } },
    { naam: "Prullenbak", icoon: "prullenbak", doe: () => { setPaletStand("dicht"); ga("/prullenbak"); } },
    { naam: "Instellingen, tags en tokens", icoon: "sleutel", doe: () => { setPaletStand("dicht"); ga("/instellingen"); } },
    { naam: "Sneltoetsen tonen", icoon: "notitie", toets: "?", doe: () => { setPaletStand("dicht"); setSneltoetsen(true); } }
  ];

  const inPrullenbak = pad.startsWith("/prullenbak");
  const inTaken = pad.startsWith("/taken");
  // Een bord van 380 pixels breed is geen bord. Daarom: zolang er geen notitie openstaat neemt
  // hij de hele breedte, en zodra je er een opent val je terug op de gewone lijst ernaast.
  const opBord = (weergave === "bord" || weergave === "tabel") && !open && !inTaken && !inPrullenbak;
  // Zonder eigen keuze: de eigenschap die je het meest gebruikt. Alfabetisch zou "deadline"
  // winnen van "status", en dan begint het bord met één kolom en een grote bak "zonder".
  const eersteSleutel =
    groepeerOp || [...eigenschappen].sort((a, b) => b.aantal - a.aantal)[0]?.sleutel || "";

  return (
    <div className={`raam${opBord ? " breed" : ""}`} data-scherm={schermSoort}>
      <Zijbalk
        open={zijbalk}
        boeken={boeken}
        tags={tags}
        zoekopdrachten={zoekopdrachten}
        huidigBoek={boek}
        huidigeTag={tag}
        inPrullenbak={inPrullenbak}
        sync={sync}
        sleept={gesleept !== null}
        legInBoek={(boekId) => {
          if (gesleeptRef.current) verplaatsNaarBoek(gesleeptRef.current, boekId);
          onthoudSleep(null);
        }}
        opZoek={() => setPaletStand("zoek")}
        naarInstellingen={() => ga("/instellingen")}
        naarBijlagen={() => ga("/bijlagen")}
        kiesTag={(naam) => {
          setTag(naam);
          setBoek(undefined);
          void laadLijst(undefined, sorteer, naam);
          ga("/");
        }}
        kiesZoekopdracht={(vraag, kijk) => {
          setPaletStand("dicht");
          // Zoals je hem bewaarde: als bord, tabel of gewoon lijst.
          if (kijk?.weergave) setWeergave(kijk.weergave as Weergave);
          if (kijk?.groepeer) setGroepeerOp(kijk.groepeer);
          void api.zoek(vraag).then((uit) => {
            setRijen(uit.treffers);
            setTag(undefined);
            setBoek(undefined);
          });
          ga("/");
        }}
        verwijderZoekopdracht={async (id) => {
          const uit = await api.verwijderZoekopdracht(id);
          setZoekopdrachten(uit.zoekopdrachten);
        }}
        kiesBoek={(id) => {
          setBoek(id);
          setTag(undefined);
          void laadLijst(id, sorteer, undefined);
          ga("/");
        }}
        naarPrullenbak={() => ga("/prullenbak")}
        naarVandaag={() => ga("/vandaag")}
        naarTaken={() => ga("/taken")}
        takenOpen={takenOpen}
        inTaken={inTaken}
        maakBoek={async (titel) => {
          try {
            const boek = await api.maakBoek(titel);
            setBoeken((huidig) => [...huidig, { ...boek, aantal: 0, kinderen: [] } as unknown as Boek]);
          } catch {
            setMelding({ tekst: "Het notitieboek kon niet worden aangemaakt.", waarschuwing: true });
          }
        }}
      />

      <div className="lijstkolom">
        <div className="lijstkop">
          <div className="nieuwveld">
            <button className="terugknop" onClick={() => setZijbalk(true)} aria-label="Menu">
              <Icoon naam="menu" grootte={18} />
            </button>
            <input
              value={nieuwTekst}
              placeholder="Nieuwe notitie…"
              onChange={(e) => setNieuwTekst(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  void nieuweNotitie(nieuwTekst.trim());
                  setNieuwTekst("");
                }
                if (e.key === "Escape") setNieuwTekst("");
              }}
            />
            <button aria-label="Nieuwe notitie" onClick={() => { void nieuweNotitie(nieuwTekst.trim()); setNieuwTekst(""); }}>
              <Icoon naam="plus" grootte={18} />
            </button>
            {sjablonen.length > 0 && (
              <select
                aria-label="Beginnen met een sjabloon"
                title="Beginnen met een sjabloon"
                value=""
                onChange={async (e) => {
                  const gekozen = sjablonen.find((s) => s.id === e.target.value);
                  if (!gekozen) return;
                  // De inhoud van het sjabloon ophalen en als nieuwe notitie neerzetten; het
                  // sjabloon zelf blijft ongemoeid.
                  const bron = await api.haal(gekozen.id);
                  await nieuweNotitie(nieuwTekst.trim() || bron.titel, undefined, bron.inhoud);
                  setNieuwTekst("");
                }}
                style={{ border: 0, background: "transparent", color: "var(--gedempt)", maxWidth: 34 }}
              >
                <option value="">▾</option>
                {sjablonen.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.titel || "Zonder titel"}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="lijstregel">
            <strong style={{ fontWeight: 600, color: "var(--inkt)" }}>
              {inTaken
                ? "Taken"
                : inPrullenbak
                ? "Prullenbak"
                : tag
                  ? `#${tag}`
                  : boek
                    ? boeken.flatMap(plat).find((b) => b.id === boek)?.titel ?? "Notities"
                    : "Alle notities"}
            </strong>
            <span>{inTaken ? (takenOpen ?? "") : rijen.length}</span>
            {(tag || boek) && !inPrullenbak && (
              <button
                className="knop stil"
                title="Filter opheffen"
                onClick={() => { setTag(undefined); setBoek(undefined); void laadLijst(undefined, sorteer, undefined); }}
              >
                <Icoon naam="kruis" grootte={13} />
              </button>
            )}
            <span style={{ flexGrow: 1 }} />
            {/* De app kijkt zelf of er elders iets veranderd is, maar soms wil je het gewoon
                nú weten — vandaar deze knop; op een telefoon is er geen adresbalk om op te
                herladen. */}
            <button
              className="knop stil"
              aria-label="Verversen"
              title="Verversen"
              onClick={() => {
                void haalNieuws();
                void laadLijst(boek, sorteer, tag);
                void ververs();
              }}
            >
              <Icoon naam="ververs" grootte={15} />
            </button>
            {!inPrullenbak && !inTaken && (
              <>
                <select value={sorteer} onChange={(e) => { setSorteer(e.target.value); void laadLijst(boek, e.target.value, tag); }} aria-label="Sorteren">
                  <option value="gewijzigd">Gewijzigd</option>
                  <option value="aangemaakt">Aangemaakt</option>
                  <option value="titel">Titel</option>
                </select>
                {/* Vier weergaven passen niet meer in één knop om op te tikken; een keuzelijst
                    zegt bovendien meteen wat er te kiezen valt. Onthouden per apparaat: op een
                    telefoon wil je iets anders dan op een breed scherm. */}
                <select
                  value={weergave}
                  aria-label="Weergave"
                  onChange={(e) => {
                    const nieuw = e.target.value as Weergave;
                    setWeergave(nieuw);
                    localStorage.setItem("notities-weergave", nieuw);
                  }}
                >
                  <option value="lijst">Lijst</option>
                  <option value="kaarten">Kaarten</option>
                  <option value="bord">Bord</option>
                  <option value="tabel">Tabel</option>
                </select>
              </>
            )}
          </div>
        </div>

        {opBord ? (
          <div className="lijstinhoud">
            <WeergaveKiezer
              weergave={weergave === "bord" ? "bord" : "tabel"}
              sleutel={eersteSleutel}
              bekend={eigenschappen}
              kies={(sleutel) => {
                setGroepeerOp(sleutel);
                localStorage.setItem("notities-groepeer", sleutel);
              }}
            />
            {eersteSleutel &&
              (weergave === "bord" ? (
                <Bord
                  rijen={rijen}
                  sleutel={eersteSleutel}
                  kies={(id) => ga(`/notitie/${id}`)}
                  zetWaarde={(id, waarde) => void zetEigenschap(id, eersteSleutel, waarde)}
                />
              ) : (
                <Tabel
                  rijen={rijen}
                  kolommen={[eersteSleutel, ...eigenschappen.map((e) => e.sleutel).filter((s) => s !== eersteSleutel)].slice(0, 5)}
                  kies={(id) => ga(`/notitie/${id}`)}
                />
              ))}
          </div>
        ) : inTaken ? (
          <Taken
            opNotitie={(id) => ga(`/notitie/${id}`)}
            opMelding={(tekst) => setMelding({ tekst, waarschuwing: true })}
            opAantal={setTakenOpen}
          />
        ) : (
        <Lijst
          rijen={rijen}
          weergave={weergave === "kaarten" ? "kaarten" : "lijst"}
          inPrullenbak={inPrullenbak}
          openId={open?.id}
          laadPrullenbak={inPrullenbak}
          kies={(id) => ga(`/notitie/${id}`)}
          opSleep={onthoudSleep}
          herstel={async (id) => {
            await zetInWachtrij({ soort: "herstel", id, tijd: new Date().toISOString() });
            await verwerkWachtrij();
            setRijen((lijst) => lijst.filter((r) => r.id !== id));
            setMelding({ tekst: "Terug uit de prullenbak" });
          }}
        />
        )}

        <button className="zweefknop" onClick={() => void nieuweNotitie("")} aria-label="Nieuwe notitie">
          <Icoon naam="plus" grootte={26} />
        </button>

        <MobielBalk pad={pad} ga={ga} opZoek={() => setPaletStand("zoek")} />
      </div>

      <div className="editorkolom">
        {open ? (
          <>
            <div className="editorkop">
              <button className="knop stil terugknop" onClick={() => ga("/")} aria-label="Terug naar de lijst">
                <Icoon naam="terug" grootte={18} />
              </button>
              <label className="pad boekkiezer" title="Verplaats deze notitie naar een notitieboek">
                {/* Als knop herkenbaar maken: als grijze tekst zag niemand dat je hier een
                    notitie naar een notitieboek verplaatst. */}
                <Icoon naam="map" grootte={14} />
                <span className="alleen-voorlezer">Notitieboek</span>
                <select
                  value={open.ouder ?? ""}
                  onChange={(e) => bewaarWijziging(open.id, { boek: e.target.value || null })}
                  style={{ border: 0, background: "transparent", color: "var(--inkt)", maxWidth: 220 }}
                >
                  <option value="">Losse notities</option>
                  {boeken.flatMap(plat).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.titel}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="knop"
                title="De koppen van deze notitie op een rij"
                onClick={() => setOverzichtOpen(true)}
              >
                <Icoon naam="lijstje" grootte={15} />
                Overzicht
              </button>
              <button
                className="knop"
                title="Hoe zag deze notitie er eerder uit?"
                onClick={() => setVersiesOpen(true)}
              >
                <Icoon naam="klok" grootte={15} />
                Versies
              </button>
              <button
                className={`knop${open.gepind ? " actief" : ""}`}
                onClick={() => bewaarWijziging(open.id, { gepind: !open.gepind })}
              >
                <Icoon naam="pin" grootte={15} />
                {open.gepind ? "Gepind" : "Pin"}
              </button>
              <button
                className={`knop${open.kluis ? " actief" : ""}`}
                title={
                  open.kluis
                    ? "Deze notitie uit de kluis halen"
                    : "Deze notitie versleutelen met je kluiswachtwoord"
                }
                onClick={() => void wisselKluis(open, !open.kluis)}
              >
                <Icoon naam="sleutel" grootte={15} />
                {open.kluis ? "In de kluis" : "Kluis"}
              </button>
              <button
                className={`knop${open.sjabloon ? " actief" : ""}`}
                title="Deze notitie als sjabloon gebruiken voor nieuwe notities"
                onClick={() => {
                  bewaarWijziging(open.id, { sjabloon: !open.sjabloon });
                  window.setTimeout(() => void ververs(), 900);
                }}
              >
                <Icoon naam="notitie" grootte={15} />
                {open.sjabloon ? "Sjabloon" : "Als sjabloon"}
              </button>
              <span className="kleuren" role="group" aria-label="Kleur van de notitie">
                {[null, "#fbe6b8", "#dce9e6", "#eadfd2", "#e6e3ef"].map((kleur) => (
                  <button
                    key={kleur ?? "geen"}
                    className={`kleurstip${(open.kleur ?? null) === kleur ? " actief" : ""}`}
                    style={{ background: kleur ?? "var(--vlak)" }}
                    aria-label={kleur ? "Kleur geven" : "Geen kleur"}
                    onClick={() => bewaarWijziging(open.id, { kleur })}
                  />
                ))}
              </span>
              <button className="knop" onClick={() => void verwijder(open.id, open.titel)}>
                <Icoon naam="prullenbak" grootte={15} />
                Weg
              </button>
            </div>

            {/* Dit blokje eromheen lijkt overbodig maar is het niet; zie .titelrij in stijl.css. */}
            <div className="titelrij">
              <div className="editorbinnen" style={{ padding: "18px 0 0" }}>
                <input
                  className="titelveld"
                  value={open.titel}
                  placeholder="Titel"
                  onChange={(e) => bewaarWijziging(open.id, { titel: e.target.value })}
                />
              </div>

              {/* `?? []` en niet `open.tags`: een notitie die nog uit de lokale cache van een
                  oudere versie van de app komt, heeft dit veld niet — en dan mag het scherm
                  niet omvallen. */}
              {(open.tags ?? []).length > 0 && (
                <div className="editorbinnen" style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                  {(open.tags ?? []).map((naam) => (
                    <button
                      key={naam}
                      className="tagchip"
                      onClick={() => {
                        setTag(naam);
                        setBoek(undefined);
                        void laadLijst(undefined, sorteer, naam);
                        ga("/");
                      }}
                    >
                      #{naam}
                    </button>
                  ))}
                </div>
              )}

              <Eigenschappen
                notitieId={open.id}
                eigenschappen={open.eigenschappen ?? {}}
                bekend={eigenschappen}
                opGewijzigd={(velden) => {
                  setOpen((huidig) => (huidig && huidig.id === open.id ? { ...huidig, eigenschappen: velden } : huidig));
                  void ververs();
                }}
                opFout={(tekst) => setMelding({ tekst, waarschuwing: true })}
              />
            </div>

            {open.kluis && !kluisInhoud ? (
              <OpSlot stand={kluisStand} opMelding={(tekst) => setMelding({ tekst, waarschuwing: true })} />
            ) : (
            <Editor
              key={`${open.id}:${herlaadEditor}`}
              notitieId={open.id}
              inhoud={open.kluis ? kluisInhoud : open.inhoud}
              zoekterm={zoekterm}
              opEditor={(editor) => { editorRef.current = editor; }}
              opMelding={(tekst) => setMelding({ tekst, waarschuwing: true })}
              opLinkZoeken={() => setPaletStand("kies")}
              opNotitieOpenen={(id) => ga(`/notitie/${id}`)}
              onWijzig={(inhoud, id) => bewaarWijziging(id, { inhoud })}
            />
            )}

            {backlinks.length > 0 && (
              <div className="backlinks">
                <div className="groep-kop" style={{ padding: "0 0 6px" }}>
                  Verwijst hiernaar ({backlinks.length})
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {backlinks.map((link) => (
                    <button key={link.id} className="backlink" onClick={() => ga(`/notitie/${link.id}`)}>
                      {link.titel || "Zonder titel"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="voetregel">
              Bijgewerkt {tijdKort(open.gewijzigd)}
              {sync.wachtend > 0 ? ` · ${sync.wachtend} wijziging${sync.wachtend === 1 ? "" : "en"} nog niet verstuurd` : " · alles bewaard"}
            </div>
          </>
        ) : (
          <div className="leeg" style={{ margin: "auto" }}>
            <p>Kies een notitie, of begin een nieuwe.</p>
            <button className="knop hoofd" onClick={() => void nieuweNotitie("")} style={{ margin: "0 auto" }}>
              <Icoon naam="plus" grootte={16} /> Nieuwe notitie
            </button>
          </div>
        )}
      </div>

      {paletStand !== "dicht" && (
        <Palet
          stand={paletStand}
          boeken={boeken}
          acties={acties}
          recente={rijen.slice(0, 8).map((r) => ({ id: r.id, titel: r.titel }))}
          sluit={() => setPaletStand("dicht")}
          kiesNotitie={(id, titel, term) => {
            if (paletStand === "kies") {
              // De gekozen notitie wordt een link in de tekst waar je gebleven was.
              editorRef.current?.chain().focus().insertContent({ type: "notitieLink", attrs: { id, titel } }).run();
              setPaletStand("dicht");
              return;
            }
            setPaletStand("dicht");
            setZoekterm(term);
            ga(`/notitie/${id}`);
          }}
          kiesTag={(naam) => {
            setPaletStand("dicht");
            setTag(naam);
            setBoek(undefined);
            void laadLijst(undefined, sorteer, naam);
            ga("/");
          }}
          kiesBoek={(id) => {
            setPaletStand("dicht");
            setTag(undefined);
            setBoek(id);
            void laadLijst(id, sorteer, undefined);
            ga("/");
          }}
          bewaarZoekopdracht={async (vraag) => {
            const naam = vraag.length > 40 ? vraag.slice(0, 40) + "…" : vraag;
            // De weergave gaat mee: een bewaarde vraag waar je als bord naar keek, hoort ook
            // als bord terug te komen. Dat is wat een "opgeslagen weergave" onderscheidt van
            // een opgeslagen zoekopdracht.
            await api.bewaarZoekopdracht(naam, vraag, { weergave, groepeer: eersteSleutel });
            await ververs();
            setPaletStand("dicht");
            setMelding({ tekst: "Zoekopdracht bewaard in de zijbalk" });
          }}
        />
      )}

      {sneltoetsen && <Sneltoetsen sluit={() => setSneltoetsen(false)} />}

      {overzichtOpen && open && <Overzicht editor={editorRef.current} sluit={() => setOverzichtOpen(false)} />}

      {versiesOpen && open && (
        <Versies
          notitieId={open.id}
          sluit={() => setVersiesOpen(false)}
          opFout={(tekst) => setMelding({ tekst, waarschuwing: true })}
          opTeruggezet={(notitie, moment) => {
            setVersiesOpen(false);
            setOpen(notitie);
            revs.current.set(notitie.id, notitie.rev);
            void bewaarNotitie(notitie);
            setRijen((lijst) =>
              lijst.map((r) => (r.id === notitie.id ? { ...r, titel: notitie.titel, gewijzigd: notitie.gewijzigd } : r))
            );
            // De editor leest zijn inhoud bij het openen; met een nieuwe sleutel begint hij
            // opnieuw, nu met de teruggezette tekst.
            setHerlaadEditor((n) => n + 1);
            setMelding({ tekst: `De versie van ${moment.toLowerCase()} staat er weer` });
          }}
        />
      )}

      {sync.afgemeld && (
        <div className="melding waarschuwing">
          Aanmelden is niet gelukt. Wat je hebt getypt staat veilig op dit apparaat en gaat
          alsnog naar de NAS zodra je binnen bent.
          {/* Naar /aanmelden en niet herladen: deze pagina staat buiten de aanmelding, dus
              alleen een pad dat er wél achter hangt brengt je bij het inlogscherm. De vlag
              gaat eerst weg, anders houdt de lus-beveiliging de poging tegen. */}
          <button
            onClick={() => {
              try {
                sessionStorage.removeItem("notities-aanmeldpoging");
              } catch {
                /* niets aan te doen */
              }
              location.href = `/aanmelden?terug=${encodeURIComponent(location.pathname + location.search)}`;
            }}
          >
            Opnieuw proberen
          </button>
        </div>
      )}

      {!sync.afgemeld && melding && (
        <div className={`melding${melding.waarschuwing ? " waarschuwing" : ""}`}>
          {melding.tekst}
          {melding.knop && <button onClick={melding.doe}>{melding.knop}</button>}
          <button onClick={() => setMelding(null)} aria-label="Sluiten">
            <Icoon naam="kruis" grootte={14} />
          </button>
        </div>
      )}

      {!sync.afgemeld && !melding && sync.laatsteFout && (
        <div className="melding waarschuwing">
          {sync.laatsteFout}
          <button onClick={() => setSync((s) => ({ ...s, laatsteFout: null }))}>Oké</button>
        </div>
      )}

      {/* Geen waarschuwing maar een mededeling: er is iets goed gegaan wat je wel moet weten,
          namelijk dat twee versies van een notitie zijn samengevoegd. */}
      {!sync.afgemeld && !melding && !sync.laatsteFout && sync.laatsteMelding && (
        <div className="melding">
          {sync.laatsteMelding}
          <button onClick={() => setSync((s) => ({ ...s, laatsteMelding: null }))}>Oké</button>
        </div>
      )}
    </div>
  );
}

function plat(boek: Boek): Boek[] {
  return [boek, ...boek.kinderen.flatMap(plat)];
}

/** De tellingen in de zijbalk na het verplaatsen van één notitie: er één bij, er één af. */
function metAangepasteTelling(boeken: Boek[], erbij: string | null, eraf: string | null): Boek[] {
  return boeken.map((b) => ({
    ...b,
    aantal: Math.max(0, b.aantal + (b.id === erbij ? 1 : 0) - (b.id === eraf ? 1 : 0)),
    kinderen: metAangepasteTelling(b.kinderen, erbij, eraf)
  }));
}

// --- zijbalk -------------------------------------------------------------------------------

function Zijbalk(props: {
  open: boolean;
  boeken: Boek[];
  tags: TagRegel[];
  zoekopdrachten: Zoekopdracht[];
  huidigBoek?: string;
  huidigeTag?: string;
  inPrullenbak: boolean;
  sync: SyncStand;
  opZoek: () => void;
  naarInstellingen: () => void;
  kiesBoek: (id?: string) => void;
  kiesTag: (naam: string) => void;
  kiesZoekopdracht: (vraag: string, kijk?: { weergave: string | null; groepeer: string | null }) => void;
  verwijderZoekopdracht: (id: string) => void;
  naarPrullenbak: () => void;
  naarBijlagen: () => void;
  naarVandaag: () => void;
  naarTaken: () => void;
  takenOpen: number | null;
  inTaken: boolean;
  maakBoek: (titel: string) => void;
  /** Er wordt op dit moment een notitie versleept; alleen dan zijn de boeken landingsplek. */
  sleept: boolean;
  legInBoek: (boekId: string) => void;
}) {
  const [nieuwBoek, setNieuwBoek] = useState<string | null>(null);
  const [sleepBoven, setSleepBoven] = useState<string | null>(null);
  const stand = props.sync.offline
    ? { klasse: "weg", tekst: "offline" }
    : props.sync.wachtend > 0
      ? { klasse: "wacht", tekst: `${props.sync.wachtend} te versturen` }
      : { klasse: "", tekst: "bijgewerkt" };

  const boekRegels = (boeken: Boek[], diep = false): React.ReactNode =>
    boeken.map((b) => (
      <div key={b.id}>
        <button
          className={`plek${diep ? " diep" : ""}${sleepBoven === b.id ? " sleepdoel" : ""}`}
          aria-current={props.huidigBoek === b.id}
          onClick={() => props.kiesBoek(b.id)}
          // Een notitie uit de lijst hierheen slepen legt hem in dit boek. `preventDefault`
          // op dragover is wat een plek tot geldige landingsplaats maakt; zonder dat weigert
          // de browser de drop en krijg je het "terugvlieg"-animatietje.
          onDragOver={(gebeurtenis) => {
            if (!props.sleept) return;
            gebeurtenis.preventDefault();
            gebeurtenis.dataTransfer.dropEffect = "move";
            setSleepBoven(b.id);
          }}
          onDragLeave={() => setSleepBoven((huidig) => (huidig === b.id ? null : huidig))}
          onDrop={(gebeurtenis) => {
            gebeurtenis.preventDefault();
            setSleepBoven(null);
            props.legInBoek(b.id);
          }}
        >
          {!diep && <Icoon naam={b.systeem === "inbox" ? "inbox" : "map"} />}
          <span style={{ flexGrow: 1 }}>{b.titel}</span>
          <span className="telling">{b.aantal || ""}</span>
        </button>
        {b.kinderen.length > 0 && boekRegels(b.kinderen, true)}
      </div>
    ));

  return (
    <nav className={`zijbalk${props.open ? " open" : ""}`}>
      <div className="merk">
        <h1>Notities</h1>
        <span style={{ flexGrow: 1 }} />
        <span className="stand">
          <span className={`stip ${stand.klasse}`} />
          {stand.tekst}
        </span>
      </div>

      <button className="zoekknop" onClick={props.opZoek}>
        <Icoon naam="zoek" grootte={16} />
        <span style={{ flexGrow: 1 }}>Zoeken</span>
        <kbd>Ctrl K</kbd>
      </button>

      <div className="groep">
        <button className="plek" aria-current={!props.huidigBoek && !props.inPrullenbak} onClick={() => props.kiesBoek(undefined)}>
          <Icoon naam="notitie" />
          <span style={{ flexGrow: 1 }}>Alle notities</span>
        </button>
        <button className="plek" onClick={props.naarVandaag}>
          <Icoon naam="kalender" />
          <span style={{ flexGrow: 1 }}>Vandaag</span>
        </button>
        <button className="plek" aria-current={props.inTaken} onClick={props.naarTaken}>
          <Icoon naam="taken" />
          <span style={{ flexGrow: 1 }}>Taken</span>
          {props.takenOpen !== null && props.takenOpen > 0 && <span className="telling">{props.takenOpen}</span>}
        </button>
      </div>

      <div className="groep">
        <div className="groep-kop" style={{ display: "flex", alignItems: "center" }}>
          <span style={{ flexGrow: 1 }}>Notitieboeken</span>
          <button
            onClick={() => setNieuwBoek("")}
            aria-label="Nieuw notitieboek"
            title="Nieuw notitieboek"
            style={{ border: 0, background: "transparent", color: "var(--gedempt)", display: "flex", padding: 2 }}
          >
            <Icoon naam="plus" grootte={15} />
          </button>
        </div>
        {boekRegels(props.boeken)}
        {nieuwBoek !== null && (
          <input
            autoFocus
            value={nieuwBoek}
            placeholder="Naam van het notitieboek"
            onChange={(e) => setNieuwBoek(e.target.value)}
            onBlur={() => setNieuwBoek(null)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && nieuwBoek.trim()) {
                props.maakBoek(nieuwBoek.trim());
                setNieuwBoek(null);
              }
              if (e.key === "Escape") setNieuwBoek(null);
            }}
            style={{ margin: "4px 10px", padding: "7px 10px", border: "1px solid var(--lijn)", borderRadius: 8, background: "var(--vlak)" }}
          />
        )}
      </div>

      {props.tags.length > 0 && (
        <div className="groep">
          <div className="groep-kop">Tags</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "2px 8px 4px" }}>
            {props.tags.slice(0, 24).map((tag) => (
              <button
                key={tag.naam}
                className={`tagchip${props.huidigeTag === tag.naam ? " actief" : ""}`}
                onClick={() => props.kiesTag(tag.naam)}
                title={`${tag.aantal} notitie${tag.aantal === 1 ? "" : "s"}`}
              >
                #{tag.naam}
              </button>
            ))}
          </div>
        </div>
      )}

      {props.zoekopdrachten.length > 0 && (
        <div className="groep">
          <div className="groep-kop">Bewaard</div>
          {props.zoekopdrachten.map((zoekopdracht) => (
            <div key={zoekopdracht.id} style={{ display: "flex", alignItems: "center" }}>
              <button
                className="plek"
                style={{ flexGrow: 1 }}
                onClick={() =>
                  props.kiesZoekopdracht(zoekopdracht.vraag, {
                    weergave: zoekopdracht.weergave,
                    groepeer: zoekopdracht.groepeer
                  })
                }
              >
                <Icoon naam="zoek" grootte={15} />
                <span style={{ flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {zoekopdracht.naam}
                </span>
              </button>
              <button
                className="knop stil"
                aria-label={`${zoekopdracht.naam} verwijderen`}
                onClick={() => props.verwijderZoekopdracht(zoekopdracht.id)}
              >
                <Icoon naam="kruis" grootte={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ flexGrow: 1 }} />

      <div className="groep">
        <button className="plek" onClick={props.naarInstellingen}>
          <Icoon naam="sleutel" />
          <span style={{ flexGrow: 1 }}>Instellingen</span>
        </button>
        <button className="plek" onClick={props.naarBijlagen}>
          <Icoon naam="paperclip" />
          <span style={{ flexGrow: 1 }}>Bijlagen</span>
        </button>
        <button className="plek" aria-current={props.inPrullenbak} onClick={props.naarPrullenbak}>
          <Icoon naam="prullenbak" />
          <span style={{ flexGrow: 1 }}>Prullenbak</span>
          <span className="telling">30 dagen</span>
        </button>
        <a className="plek" href="/api/export" download>
          <Icoon naam="download" />
          <span style={{ flexGrow: 1 }}>Alles exporteren</span>
        </a>
      </div>
    </nav>
  );
}

// --- lijst ---------------------------------------------------------------------------------

function Lijst(props: {
  rijen: Rij[];
  weergave: "lijst" | "kaarten";
  inPrullenbak: boolean;
  laadPrullenbak: boolean;
  openId?: string;
  kies: (id: string) => void;
  herstel: (id: string) => void;
  /** Welke notitie je op dit moment versleept — `null` zodra je loslaat. */
  opSleep: (id: string | null) => void;
}) {
  const [bakRijen, setBakRijen] = useState<Rij[] | null>(null);

  useEffect(() => {
    if (!props.laadPrullenbak) {
      setBakRijen(null);
      return;
    }
    void api
      .prullenbak()
      .then(({ rijen }) => setBakRijen(rijen))
      .catch(() => setBakRijen([]));
  }, [props.laadPrullenbak]);

  const tonen = props.inPrullenbak ? (bakRijen ?? []) : props.rijen;

  const groepen = useMemo(() => {
    const kaart = new Map<string, Rij[]>();
    for (const rij of tonen) {
      const naam = props.inPrullenbak ? "In de prullenbak" : groepVan(rij);
      if (!kaart.has(naam)) kaart.set(naam, []);
      kaart.get(naam)!.push(rij);
    }
    return [...kaart.entries()];
  }, [tonen, props.inPrullenbak]);

  if (tonen.length === 0) {
    return (
      <div className="lijstinhoud">
        <p className="leeg">
          {props.inPrullenbak ? "De prullenbak is leeg." : "Nog niets hier. Typ hierboven om te beginnen."}
        </p>
      </div>
    );
  }

  const kiesOfHerstel = (id: string) => (props.inPrullenbak ? props.herstel(id) : props.kies(id));

  // Een div met een knoprol, en geen echte <button>: een knop is in meerdere browsers een
  // onwillige sleepbron (Firefox begint er helemaal geen sleep mee). En slepen naar een
  // notitieboek is precies wat deze rij moet kunnen.
  const regel = (rij: Rij, alsKaart: boolean) => (
    <div
      key={rij.id}
      role="button"
      tabIndex={0}
      className={alsKaart ? "kaart" : "rij"}
      aria-current={rij.id === props.openId}
      style={rij.kleur ? { background: rij.kleur, borderColor: rij.kleur } : undefined}
      onClick={() => kiesOfHerstel(rij.id)}
      onKeyDown={(gebeurtenis) => {
        if (gebeurtenis.key === "Enter" || gebeurtenis.key === " ") {
          gebeurtenis.preventDefault();
          kiesOfHerstel(rij.id);
        }
      }}
      // De titel gaat als tekst mee in de sleep: zonder inhoud begint een sleep in sommige
      // browsers niet eens.
      draggable={!props.inPrullenbak}
      onDragStart={(gebeurtenis) => {
        gebeurtenis.dataTransfer.setData("text/plain", rij.titel || "Zonder titel");
        gebeurtenis.dataTransfer.effectAllowed = "move";
        props.opSleep(rij.id);
      }}
      onDragEnd={() => props.opSleep(null)}
    >
      <span className="titel">{rij.titel || "Zonder titel"}</span>
      {rij.fragment && <span className="fragment">{rij.fragment}</span>}
      {(rij.tags ?? []).length > 0 && (
        <span className="rijtags">
          {(rij.tags ?? []).slice(0, 3).map((t) => (
            <span key={t}>#{t}</span>
          ))}
        </span>
      )}
      <span className="meta">
        {rij.taken.totaal > 0 && (
          <span>
            {rij.taken.af} van {rij.taken.totaal} af ·{" "}
          </span>
        )}
        {tijdKort(rij.gewijzigd)}
        {props.inPrullenbak && " · tik om terug te halen"}
      </span>
    </div>
  );

  if (props.weergave === "kaarten" && !props.inPrullenbak) {
    // De kaartweergave van Keep: alles naast elkaar, in één oogopslag te overzien.
    return (
      <div className="lijstinhoud">
        <div className="kaarten">{tonen.map((rij) => regel(rij, true))}</div>
      </div>
    );
  }

  return (
    <div className="lijstinhoud">
      {groepen.map(([naam, rijen]) => (
        <div key={naam} className="groep">
          <div className="groep-kop">{naam}</div>
          {rijen.map((rij) => regel(rij, false))}
        </div>
      ))}
    </div>
  );
}

// --- delen-naar ------------------------------------------------------------------------------

function Vastleggen(props: { inbox: string | null; ga: (pad: string) => void }) {
  const vraag = new URLSearchParams(location.search);
  const [titel, setTitel] = useState(vraag.get("titel") ?? "");
  const [tekst, setTekst] = useState(vraag.get("tekst") ?? "");
  const url = vraag.get("url") ?? "";
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  /** De hele pagina laten ophalen door de NAS. Dit is het telefoonpad van de web clipper: op de
      laptop stuurt de bookmarklet de html mee, hier heeft de app alleen het adres. */
  async function knip() {
    setBezig(true);
    setFout(null);
    try {
      const uit = await api.clip({ url, titel: titel || undefined, boek: props.inbox });
      await bewaarNotitie(uit.notitie);
      props.ga(`/notitie/${uit.notitie.id}`);
    } catch (f) {
      // Eerlijk zijn over wat er misging — een pagina achter een aanmelding of een leeslimiet
      // krijgt de NAS niet te zien, en dan is "alleen de link" het antwoord.
      setFout(`${(f as Error).message}. Bewaar anders alleen de link.`);
      setBezig(false);
    }
  }

  async function bewaar() {
    setBezig(true);
    try {
      const notitie = await api.vastleggen({ titel, tekst, url, boek: props.inbox });
      await bewaarNotitie(notitie);
      props.ga(`/notitie/${notitie.id}`);
    } catch {
      // Geen verbinding: dan alsnog lokaal aanmaken en de wachtrij het laten doen.
      const id = ulid();
      const nu = new Date().toISOString();
      const inhoud: Blok = {
        type: "doc",
        content: [
          ...(tekst ? [{ type: "paragraph", content: [{ type: "text", text: tekst }] }] : []),
          ...(url ? [{ type: "paragraph", content: [{ type: "text", text: url, marks: [{ type: "link", attrs: { href: url } }] }] }] : [])
        ]
      };
      await zetInWachtrij({ soort: "maak", id, lading: { titel, inhoud, boek: props.inbox }, tijd: nu });
      props.ga(`/notitie/${id}`);
    }
  }

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
      <h2 style={{ fontFamily: "var(--schrift-titel)", fontSize: 22, margin: 0 }}>Bewaren in Notities</h2>
      {url && (
        <div style={{ display: "flex", gap: 12, padding: 12, border: "1px solid var(--lijn)", borderRadius: 12, background: "var(--lijstvlak)" }}>
          <Icoon naam="wolk" grootte={20} />
          <span style={{ fontSize: 13, color: "var(--gedempt)", overflowWrap: "anywhere" }}>{url}</span>
        </div>
      )}
      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--gedempt)" }}>
        Titel
        <input value={titel} onChange={(e) => setTitel(e.target.value)} style={{ width: "100%", height: 46, padding: "0 12px", border: "1px solid var(--lijn)", borderRadius: 12, marginTop: 6 }} />
      </label>
      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--gedempt)" }}>
        Tekst
        <textarea value={tekst} onChange={(e) => setTekst(e.target.value)} rows={6} style={{ width: "100%", padding: 12, border: "1px solid var(--lijn)", borderRadius: 12, marginTop: 6, fontFamily: "inherit" }} />
      </label>
      {fout && (
        <p style={{ margin: 0, fontSize: 13, padding: 10, borderRadius: 10, color: "#8c440f", background: "#fbf0e6" }} role="alert">
          {fout}
        </p>
      )}
      {url ? (
        <>
          <button className="knop hoofd" style={{ height: 50, justifyContent: "center", fontSize: 16 }} disabled={bezig} onClick={() => void knip()}>
            {bezig ? "Bezig met ophalen…" : "Hele pagina bewaren"}
          </button>
          <button className="knop" style={{ height: 44, justifyContent: "center" }} disabled={bezig} onClick={() => void bewaar()}>
            Alleen de link bewaren
          </button>
        </>
      ) : (
        <button className="knop hoofd" style={{ height: 50, justifyContent: "center", fontSize: 16 }} disabled={bezig} onClick={() => void bewaar()}>
          Bewaren in de Inbox
        </button>
      )}
      <p style={{ fontSize: 12, color: "var(--gedempt)", display: "flex", gap: 8, alignItems: "flex-start" }}>
        <Icoon naam="wolk" grootte={15} />
        Geen bereik? De notitie staat meteen op je telefoon en gaat vanzelf naar de NAS zodra je weer verbinding hebt.
      </p>
    </div>
  );
}

// --- mobiele balk ------------------------------------------------------------------------------

function MobielBalk(props: { pad: string; ga: (pad: string) => void; opZoek: () => void }) {
  return (
    <div className="mobielbalk">
      <button aria-current={props.pad === "/"} onClick={() => props.ga("/")}>
        <Icoon naam="notitie" grootte={21} />
        Notities
      </button>
      <button onClick={props.opZoek}>
        <Icoon naam="zoek" grootte={21} />
        Zoeken
      </button>
      <button onClick={() => props.ga("/vandaag")}>
        <Icoon naam="kalender" grootte={21} />
        Vandaag
      </button>
      {/* Taken staat hier en niet alleen in de zijbalk: op een telefoon is dit de plek waar je
          "wat moet ik nog" opzoekt, en de zijbalk zit achter een hamburger. */}
      <button aria-current={props.pad.startsWith("/taken")} onClick={() => props.ga("/taken")}>
        <Icoon naam="taken" grootte={21} />
        Taken
      </button>
      <button aria-current={props.pad.startsWith("/prullenbak")} onClick={() => props.ga("/prullenbak")}>
        <Icoon naam="prullenbak" grootte={21} />
        Prullenbak
      </button>
    </div>
  );
}
